import type { IPlayer } from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/Players";
import { useState, useEffect, useRef, type SetStateAction, type Dispatch, useContext, useCallback } from 'react';
import CloseIconLight from '@ihs-widget/assets/svg/lineup/close_light.svg';
import CloseIconDark from '@ihs-widget/assets/svg/lineup/close_dark.svg';
import { useTheme } from "@ihs-widget/stores/ThemeContext";
import { useLanguage } from "@ihs-widget/utils/hooks/useLanguage";
import { useTranslations } from "../../../../../../translation/TranslationContext";
import { T } from "@ihs-widget/utils/enums";
import { useColorContext } from "@ihs-widget/stores/ColorContext";
import { useVersion } from "@ihs-widget/utils/hooks/useVersion";
import { type IState, StateContext } from "../../../../../../state/stateContext";

interface ITooltipProps {
    player: IPlayer;
    parentWidth: string;
    containerRef: React.RefObject<HTMLDivElement | null>;
    playerCenterX: number;
    setShowTooltip: Dispatch<SetStateAction<boolean>>;
    isHorizontalList?: boolean;
}

const PlayerTooltip = ({ player, parentWidth, containerRef, playerCenterX, setShowTooltip, isHorizontalList = false }: ITooltipProps) => {
    const { theme, version } = useTheme();
    const tooltipRef = useRef<HTMLDivElement>(null);
    const nameRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ left: 0, top: 68, tooltipHeight: 76 });
    const [arrowPosition, setArrowPosition] = useState(0);
    const [tooltipWidth, setTooltipWidth] = useState(230);
    const parentWidthNum = parseInt(parentWidth);
    const VIRTUAL_SPACE_VERTICAL = 200;
    const VIRTUAL_SPACE_HORIZONTAL = 5;
    const MIN_TOOLTIP_WIDTH = 230;
    const MAX_TOOLTIP_WIDTH = 250;
    const [isNameTruncated, setIsNameTruncated] = useState(false);
    const { colors } = useColorContext();
    const { isV1 } = useVersion();
    const ctx = useContext(StateContext);
    const { setStContext } = ctx || {};

    const [isPositionCalculated, setIsPositionCalculated] = useState(false);

    const language = useLanguage();
    const t = useTranslations(language);

    const getTextColor = () => {
        if (isV1) {
            return theme === T.LIGHT ? '#FFFFFF' : '#000000';
        }
        return colors?.textColor;
    };

    const getNameTextColor = () => {
        if (isV1) {
            return theme === T.LIGHT ? '#FFFFFF' : '#111111';
        }
        return colors?.textColor;
    };


    const getDescriptionColor = () => {
        if (isV1) {
            return theme === T.LIGHT ? '#CCCCCC' : '#6B6B6B'; // base090 dark:text-base0
        }
        return theme === T.LIGHT ? '#6B6B6B' : '#D9D9D9';
    };

    const getCloseIcon = () => {
        if (isV1) {
            return theme === T.LIGHT ? CloseIconLight : CloseIconDark;
        }
        return theme === T.LIGHT ? CloseIconDark : CloseIconLight;
    };

    const getStyles = () => ({
        textColor: getTextColor(),
        nameTextColor: getNameTextColor(),
        descriptionColor: getDescriptionColor(),
        backgroundColor: isV1 ? colors?.reverseBackgroundColor : colors?.backgroundColor,
        borderColor: isV1 ? colors?.reverseBackgroundColor : colors?.backgroundColor,
        arrowColor: isV1 ? colors?.reverseBackgroundColor : colors?.backgroundColor
    });

    useEffect(() => {
        if (nameRef.current) {
            const nameWidth = nameRef.current.scrollWidth;
            const containerWidth = nameRef.current.offsetWidth;

            setIsNameTruncated(nameWidth > containerWidth);

            if (nameWidth > containerWidth) {
                const requiredWidth = Math.min(nameWidth + 44, MAX_TOOLTIP_WIDTH);
                setTooltipWidth(Math.max(MIN_TOOLTIP_WIDTH, requiredWidth));
            }
        }
    }, [player.name, player.first_name, player.last_name]);

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        setStContext((prev: IState) => ({
            ...prev,
            activeTooltipPlayerId: null
        }));
        setShowTooltip(false);
    };

    const calculatePosition = useCallback(() => {
        if (tooltipRef.current && containerRef?.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const parentRect = tooltipRef.current.parentElement?.getBoundingClientRect();

            if (parentRect) {
                let left = (parentWidthNum - tooltipWidth) / 2;
                let top = 68;

                if (isHorizontalList) {
                    left = playerCenterX - (tooltipWidth / 2);
                    top = 68;
                    // Check if tooltip is not out of container
                    const rightEdge = left + tooltipWidth;
                    const containerWidth = containerRef.current?.clientWidth || window.innerWidth;
                    if (rightEdge > containerWidth - 10) {
                        left = containerWidth - tooltipWidth - 10;
                    }
                    if (left < 10) {
                        left = 10;
                    }
                } else {
                    //Logic for footballField
                    const rightEdge = parentRect.left + left + tooltipWidth - containerRect.left;
                    if (rightEdge > containerRect.width + VIRTUAL_SPACE_HORIZONTAL) {
                        left -= rightEdge - containerRect.width - VIRTUAL_SPACE_HORIZONTAL + 12;
                    }

                    const leftEdge = parentRect.left + left - containerRect.left;
                    if (leftEdge < -VIRTUAL_SPACE_HORIZONTAL) {
                        left += -VIRTUAL_SPACE_HORIZONTAL - leftEdge + 12;
                    }

                    const bottomEdge = parentRect.top + top + tooltipRect.height - containerRect.top;
                    if (bottomEdge > containerRect.height + VIRTUAL_SPACE_VERTICAL - 12) {
                        top = -tooltipRect.height;
                    }
                }

                setPosition({ left: left, top, tooltipHeight: 76 });

                if (isHorizontalList) {
                    const arrowPos = playerCenterX - left - 3;
                    setArrowPosition(Math.max(8, Math.min(tooltipWidth - 16, arrowPos)));
                } else {
                    setArrowPosition(Math.max(8, Math.min(tooltipWidth - 16, playerCenterX - (parentRect.left + left))));
                }
            }
        }
    }, [tooltipWidth, parentWidthNum, isHorizontalList, playerCenterX, containerRef]);

    useEffect(() => {
        calculatePosition();
    }, [calculatePosition]);

    useEffect(() => {
        if (tooltipRef.current) {
            setIsPositionCalculated(true);
        }
    }, [position]);

    return (
        <div
            data-testid={`player-tooltip-${player.name}`}
            ref={tooltipRef}
            className={`absolute shadow-lg rounded-md border 
            flex flex-col justify-start items-start p-[8px] transition-opacity duration-100 ${isHorizontalList ? 'tooltip-horizontal-list' : ''}`}
            style={{
                width: `${tooltipWidth}px`,
                left: `${position.left}px`,
                top: `${position.top - 9}px`,
                height: 'auto',
                minHeight: `${position.tooltipHeight}px`,
                backgroundColor: getStyles().backgroundColor,
                borderColor: getStyles().borderColor,
                zIndex: 9999,
                opacity: isPositionCalculated ? 1 : 0
            }}
        >
            <div
                data-testid={`player-tooltip-arrow`}
                className={`absolute w-0 h-0 
                          border-l-[6px] border-l-transparent 
                          border-r-[6px] border-r-transparent 
                          border-b-[6px] 
                          top-[-6px]`}
                style={{
                    left: `${arrowPosition}px`,
                    borderBottomColor: getStyles().arrowColor
                }}
            />
            <div data-testid={`player-tooltip-name`} className={`w-full h-[20px] font-robotoRegular font-normal text-[14px] leading-[20px] 
                            flex ${isNameTruncated ? 'justify-start' : 'justify-center'} items-center mb-[8px] truncate`}
                style={{
                    color: getStyles().nameTextColor
                }}
            >
                <div data-testid={`player-tooltip-name-wrapper`}
                    ref={nameRef}
                    className={`truncate w-[calc(100%-28px)] ${isNameTruncated ? '' : 'flex justify-center'}`}>
                    {player.first_name} {player.last_name}
                </div>
                <div data-testid={`player-tooltip-close`} className="absolute right-0 top-0 w-[28px] h-[28px] flex justify-center items-center cursor-pointer z-40"
                    onClick={handleClose}>
                    <img src={getCloseIcon()} alt="close" />
                </div>
            </div>
            <div data-testid={`player-tooltip-content`} className="w-full flex flex-row justify-between items-end gap-[16px]">
                <div data-testid={`player-tooltip-position`} className="w-[30%] max-w-[70px] font-robotoMedium font-medium text-[12px] leading-[16px]
                                flex flex-col justify-center items-center"
                    style={{
                        color: getStyles().textColor
                    }}
                >
                    <div data-testid={`player-tooltip-position-text`}
                        className="w-full text-center max-h-[32px] overflow-hidden relative leading-[16px] wrap-break-word "
                        style={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,

                        }}
                    >
                        {t[player.position_label as keyof typeof t]}
                    </div>
                    <div data-testid={`player-tooltip-position-label`}
                        className="w-full truncate text-center"
                        style={{
                            color: getStyles().descriptionColor
                        }}
                    >
                        {t[`InHouseStats.LineupPosition`]}
                    </div>
                </div>
                <div data-testid={`player-tooltip-preferred-foot`} className="w-[50%] max-w-[111px] font-robotoMedium font-medium text-[12px] leading-[16px] flex flex-col justify-center items-center"
                    style={{
                        color: getStyles().textColor
                    }}
                >
                    <div data-testid={`player-tooltip-preferred-foot-text`} className="w-full text-center line-clamp-2">
                        {t[player.preferredFoot as keyof typeof t]}
                    </div>
                    <div data-testid={`player-tooltip-preferred-foot-label`} className="w-full truncate text-center"
                        style={{
                            color: getStyles().descriptionColor
                        }}
                    >
                        {t[`InHouseStats.LineupPreferredFoot`]}
                    </div>
                </div>
                <div data-testid={`player-tooltip-age`} className="w-[20%] max-w-[41px] font-robotoMedium font-medium text-[12px] leading-[16px] flex flex-col justify-between items-start h-full"
                    style={{
                        color: getStyles().textColor
                    }}
                >
                    <div data-testid={`player-tooltip-age-text`} className="w-full text-center line-clamp-2">{player.age}</div>
                    <div data-testid={`player-tooltip-age-label`} className="w-full truncate text-center"
                        style={{
                            color: getStyles().descriptionColor
                        }}
                    >
                        {t[`InHouseStats.LineupAge`]}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlayerTooltip;